import { lazy, useMemo } from "react";
import { RouteSuspense } from "@/shared/routing/RouteSuspense";
import { Navigate, useSearchParams, useParams } from "react-router-dom";
import type { RouteObject } from "react-router-dom";

import {
  readFiniteIntParam,
  readSearchParam,
  type SearchParams,
} from "@/lib/routing/search-params";
import { useLocale } from "@seller/app/locale";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { getSellerPath } from "@/lib/routing/portal-urls";

const ProductsPage = lazy(() => import("./pages/ProductsPage").then(module => ({ default: module.ProductsPage })));
const ProductEditPage = lazy(() => import("./pages/ProductEditPage").then(module => ({ default: module.ProductEditPage })));

function getSearchParamsRecord(searchParams: URLSearchParams): SearchParams {
  const entries = Array.from(searchParams.entries());

  return entries.reduce<SearchParams>((acc, [key, value]) => {
    const currentValue = acc[key];

    if (typeof currentValue === "undefined") {
      acc[key] = value;
      return acc;
    }

    if (Array.isArray(currentValue)) {
      acc[key] = [...currentValue, value];
      return acc;
    }

    acc[key] = [currentValue, value];
    return acc;
  }, {});
}

function ProductsRoute() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const [searchParams] = useSearchParams();
  const resolvedSearchParams = useMemo(
    () => getSearchParamsRecord(searchParams),
    [searchParams],
  );
  const categoryIds = searchParams.getAll("categoryIds[]");
  const availability = readSearchParam(resolvedSearchParams, "availability") ?? "";
  const status = readSearchParam(resolvedSearchParams, "status") ?? "";
  const search = readSearchParam(resolvedSearchParams, "search") ?? "";
  const notice = readSearchParam(resolvedSearchParams, "notice") ?? "";
  // The featured VIEW: the merchant's own merchandising order, served by the
  // API rather than sorted here, so it stays right across pages.
  const featured = readSearchParam(resolvedSearchParams, "featured") === "1";
  const limit = readFiniteIntParam(resolvedSearchParams, "limit", {
    defaultValue: 20,
    min: 1,
    max: 100,
  });
  const offset = readFiniteIntParam(resolvedSearchParams, "offset", {
    defaultValue: 0,
    min: 0,
  });

  return (
    <ProductsPage
      locale={locale}
      storeSlug={storeSlug}
      categoryIds={categoryIds}
      availability={availability}
      status={status}
      search={search}
      notice={notice}
      featured={featured}
      limit={limit}
      offset={offset}
    />
  );
}

function ProductEditRoute() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const { id = "" } = useParams();

  return (
    <ProductEditPage
      locale={locale}
      storeSlug={storeSlug}
      productId={id}
    />
  );
}

/**
 * `/products/new` — a typed or bookmarked "new product" address. Creating
 * happens in the list's drawer (`?create=1`), so this forwards there. The
 * route registry admits it only with PRODUCT_CREATE (`store-access.ts`); it
 * must be declared, or `products/:id` would load a product called "new".
 */
function ProductCreateRoute() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  return (
    <Navigate to={`${getSellerPath(locale, storeSlug)}/products?create=1`} replace />
  );
}

export const productRoutes: RouteObject[] = [
  {
    path: "products",
    element: <RouteSuspense><ProductsRoute /></RouteSuspense>,
  },
  {
    path: "products/new",
    element: <ProductCreateRoute />,
  },
  {
    path: "products/:id",
    element: <RouteSuspense><ProductEditRoute /></RouteSuspense>,
  },
];
